import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { subscriptionAPI } from '../utils/api';
import SubscriptionModal from '../components/SubscriptionModal/SubscriptionModal';

export const SUBSCRIPTION_REQUIRED_EVENT = 'avivion:subscription-required';

interface SubscriptionContextType {
  hasSubscription: boolean;
  requireSubscription: (action?: () => void) => boolean;
  openSubscriptionModal: () => void;
  closeSubscriptionModal: () => void;
  purchaseSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export const dispatchSubscriptionRequired = () => {
  window.dispatchEvent(new CustomEvent(SUBSCRIPTION_REQUIRED_EVENT));
};

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { user, loading, refreshUser, setUser } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);

  const hasSubscription = !!user?.has_subscription || !!user?.is_admin;
  const monthlyPrice = user?.subscription_monthly_price ?? 499;
  const balance = user?.balance ?? 0;

  const openSubscriptionModal = useCallback(() => {
    if (!loading && !user?.is_admin) {
      setModalOpen(true);
    }
  }, [loading, user?.is_admin]);

  const closeSubscriptionModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const purchaseSubscription = useCallback(async () => {
    const response = await subscriptionAPI.purchase();
    if (response.success && response.user) {
      setUser(response.user);
    } else {
      throw new Error('Не удалось подключить подписку');
    }
    await refreshUser();
  }, [refreshUser, setUser]);

  const requireSubscription = useCallback(
    (action?: () => void) => {
      if (loading) return false;
      if (hasSubscription) {
        action?.();
        return true;
      }
      setModalOpen(true);
      return false;
    },
    [hasSubscription, loading]
  );

  useEffect(() => {
    const handler = () => {
      refreshUser();
      openSubscriptionModal();
    };
    window.addEventListener(SUBSCRIPTION_REQUIRED_EVENT, handler);
    return () => window.removeEventListener(SUBSCRIPTION_REQUIRED_EVENT, handler);
  }, [openSubscriptionModal, refreshUser]);

  return (
    <SubscriptionContext.Provider
      value={{
        hasSubscription,
        requireSubscription,
        openSubscriptionModal,
        closeSubscriptionModal,
        purchaseSubscription,
      }}
    >
      {children}
      <SubscriptionModal
        open={modalOpen}
        onClose={closeSubscriptionModal}
        balance={balance}
        monthlyPrice={monthlyPrice}
        hasSubscription={!!user?.has_subscription}
        onPurchase={purchaseSubscription}
      />
    </SubscriptionContext.Provider>
  );
};
